import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  structuredData?: object;
}

/**
 * SEO Meta Tags Component
 * Dynamically updates page meta tags for better search engine optimization
 */
export function SEOMeta({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  canonicalUrl,
  structuredData
}: SEOMetaProps) {
  const location = useLocation();
  const baseUrl = 'https://www.stockfortress.com';
  const fullUrl = canonicalUrl || `${baseUrl}${location.pathname}`;

  useEffect(() => {
    // Update page title
    if (title) {
      document.title = title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (selector: string, attribute: string, content: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.split('"')[1]);
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.split('"')[1]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, content);
    };

    // Update description
    if (description) {
      updateMetaTag('meta[name="description"]', 'content', description);
      updateMetaTag('meta[property="og:description"]', 'content', ogDescription || description);
      updateMetaTag('meta[name="twitter:description"]', 'content', ogDescription || description);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('meta[name="keywords"]', 'content', keywords);
    }

    // Update Open Graph tags
    if (ogTitle || title) {
      updateMetaTag('meta[property="og:title"]', 'content', ogTitle || title);
      updateMetaTag('meta[name="twitter:title"]', 'content', ogTitle || title);
    }

    if (ogImage) {
      updateMetaTag('meta[property="og:image"]', 'content', ogImage);
      updateMetaTag('meta[name="twitter:image"]', 'content', ogImage);
    }

    updateMetaTag('meta[property="og:url"]', 'content', fullUrl);
    updateMetaTag('meta[property="og:type"]', 'content', ogType);
    updateMetaTag('meta[property="og:site_name"]', 'content', 'Stock Fortress');

    // Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    updateMetaTag('meta[name="twitter:site"]', 'content', '@stockfortress');

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = fullUrl;

    // Structured Data (JSON-LD)
    if (structuredData) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(structuredData);
    }
  }, [title, description, keywords, ogTitle, ogDescription, ogImage, ogType, fullUrl, structuredData]);

  return null; // This component doesn't render anything
}

/**
 * Generate SEO metadata for stock pages
 */
export function getStockSEOMeta(symbol: string, section: string = 'overview', companyName?: string) {
  const sectionTitles: Record<string, string> = {
    overview: 'Overview & Analysis',
    projections: 'Financial Projections',
    analysis: 'Technical Analysis',
    financials: 'Financial Statements',
    earnings: 'Earnings Reports',
    forecast: 'Forecasts & Predictions',
    summary: 'Summary',
    'swot-analysis': 'SWOT Analysis',
    'analyst-corner': 'Analyst Ratings'
  };

  const sectionDescriptions: Record<string, string> = {
    overview: `Comprehensive overview and analysis of ${symbol} stock including key metrics, performance data, and investment insights.`,
    projections: `Financial projections and future estimates for ${symbol} stock. View revenue forecasts, earnings predictions, and growth analysis.`,
    analysis: `In-depth technical analysis of ${symbol} stock with charts, trends, and trading signals for informed investment decisions.`,
    financials: `Complete financial statements for ${symbol} including income statement, balance sheet, and cash flow analysis.`,
    earnings: `Latest earnings reports and quarterly results for ${symbol} stock with historical performance data.`,
    forecast: `Professional forecasts and price predictions for ${symbol} stock from top analysts and investment firms.`,
    summary: `Quick summary and key insights for ${symbol} stock including price, performance, and fundamental metrics.`,
    'swot-analysis': `SWOT Analysis for ${symbol} stock - Strengths, Weaknesses, Opportunities, and Threats assessment.`,
    'analyst-corner': `Analyst ratings and price targets for ${symbol} stock from leading investment research firms.`
  };

  const title = companyName
    ? `${symbol} - ${companyName} ${sectionTitles[section] || ''} | Stock Fortress`
    : `${symbol} Stock ${sectionTitles[section] || ''} | Stock Fortress`;

  const description = sectionDescriptions[section] || `View detailed ${section} information for ${symbol} stock on Stock Fortress.`;

  const keywords = `${symbol} stock, ${symbol} stock analysis, ${symbol} ${section}, ${symbol} investment, stock fortress, stock market analysis, ${companyName || symbol} stock`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    "name": `${symbol} Stock ${sectionTitles[section] || ''}`,
    "description": description,
    "url": `https://www.stockfortress.com/${symbol}/${section}`,
    "category": "Stock Analysis",
    "provider": {
      "@type": "Organization",
      "name": "Stock Fortress",
      "url": "https://www.stockfortress.com"
    }
  };

  const imageText = encodeURIComponent(`${symbol} ${sectionTitles[section] || 'Stock Analysis'}`);
  const ogImage = `https://placehold.co/1200x630/0f172a/ffffff.png?text=${imageText}&font=roboto`;

  return {
    title,
    description,
    keywords,
    ogImage,
    structuredData
  };
}

/**
 * Generate SEO metadata for tool pages
 */
export function getToolSEOMeta(toolName: string) {
  const toolMetadata: Record<string, { title: string; description: string; keywords: string }> = {
    'chart-analysis': {
      title: 'Stock Chart Analysis Tool | Stock Fortress',
      description: 'Advanced stock chart analysis with technical indicators, pattern recognition, and real-time market data. Analyze stocks like a pro with Stock Fortress.',
      keywords: 'stock chart analysis, technical analysis, stock charts, trading tools, market analysis, stock fortress'
    },
    'screener': {
      title: 'Stock Screener - Find Top Stocks | Stock Fortress',
      description: 'Powerful stock screener to filter and find the best stocks based on your criteria. Screen by fundamentals, technicals, and performance metrics.',
      keywords: 'stock screener, stock filter, find stocks, best stocks, stock search, investment screening, stock fortress'
    },
    'compare': {
      title: 'Compare Stocks Side by Side | Stock Fortress',
      description: 'Compare stocks side-by-side with detailed metrics, charts, and analysis. Make informed investment decisions by comparing multiple stocks.',
      keywords: 'compare stocks, stock comparison, stock vs stock, investment comparison, stock analysis, stock fortress'
    },
    'simulator': {
      title: 'Stock Investment Simulator | Stock Fortress',
      description: 'Practice trading with our stock investment simulator. Test strategies, track performance, and learn to invest without risking real money.',
      keywords: 'stock simulator, investment simulator, trading simulator, paper trading, practice trading, stock fortress'
    },
    'market-movers': {
      title: 'Market Movers - Top Gainers & Losers | Stock Fortress',
      description: 'Track today\'s top market movers including biggest gainers, losers, and most active stocks. Real-time market movement data.',
      keywords: 'market movers, top gainers, top losers, stock movers, active stocks, market activity, stock fortress'
    },
    'hall-of-fame': {
      title: 'Hall of Fame - Top Investors & Stock Picks | Stock Fortress',
      description: 'Discover top investors and their best stock picks. Learn from successful traders and track their portfolio performance.',
      keywords: 'hall of fame, top investors, best stock picks, successful traders, investment leaderboard, stock fortress'
    },
    'earnings': {
      title: 'Earnings Calendar & Reports | Stock Fortress',
      description: 'Track upcoming earnings announcements, historical earnings reports, and earnings call transcripts for all major stocks.',
      keywords: 'earnings calendar, earnings reports, earnings announcements, quarterly earnings, earnings calls, stock fortress'
    },
    'news': {
      title: 'Stock Market News & Updates | Stock Fortress',
      description: 'Latest stock market news, company updates, and financial headlines. Stay informed with real-time market news.',
      keywords: 'stock market news, financial news, market updates, stock news, investment news, stock fortress'
    },
    'etfs': {
      title: 'ETF Analysis & Tracking | Stock Fortress',
      description: 'Comprehensive ETF analysis including holdings, performance, and comparison tools. Track and analyze ETFs effectively.',
      keywords: 'etf analysis, etf tracking, exchange traded funds, etf holdings, etf performance, stock fortress'
    },
    'fund-insights': {
      title: 'Fund Insights - Institutional Holdings | Stock Fortress',
      description: 'Track institutional fund holdings, super investor portfolios, and hedge fund positions. Follow the smart money.',
      keywords: 'fund insights, institutional holdings, hedge funds, super investors, fund portfolios, stock fortress'
    },
    'insider-trades': {
      title: 'Insider Trading Activity | Stock Fortress',
      description: 'Monitor insider trading activity including buys, sells, and SEC Form 4 filings. Track what company insiders are doing.',
      keywords: 'insider trading, insider buys, insider sells, sec form 4, insider activity, stock fortress'
    },
    'senate-trades': {
      title: 'Senate Stock Trades | Stock Fortress',
      description: 'Track stock trades made by U.S. Senators and Representatives. Monitor congressional trading activity and disclosures.',
      keywords: 'senate trades, congressional trading, politician stocks, senate stock trades, government trades, stock fortress'
    }
  };

  return toolMetadata[toolName] || {
    title: `${toolName} | Stock Fortress`,
    description: `Use Stock Fortress ${toolName} tool for comprehensive stock market analysis and insights.`,
    keywords: `${toolName}, stock analysis, investment tools, stock fortress`
  };
}

